import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { DefaultMeetingSession, MeetingSessionConfiguration } from 'amazon-chime-sdk-js'

export type MeetingSessionState = {
  session: DefaultMeetingSession | null
  configuration: MeetingSessionConfiguration | null
  appointmentId: string | null
  meetingId: string | null
  attendeeId: string | null
  topic: string | null
  shouldStartVideo: boolean
  isActiveMeeting: () => boolean
  setSession: (data: {
    session: DefaultMeetingSession | null
    appointmentId?: string | null
    meetingId: string
    attendeeId: string
    topic?: string | null
    startVideo?: boolean
  }) => void
  setPreparation: (data: {
    configuration: MeetingSessionConfiguration
    appointmentId?: string | null
    meetingId: string
    attendeeId: string
    topic?: string | null
  }) => void
  setShouldStartVideo: (value: boolean) => void
  clearSession: () => void
  clearMeetingSession: () => void
}

export const useMeetingStore = create<MeetingSessionState>()(
  devtools(
    (set, get) => ({
      session: null,
      configuration: null,
      appointmentId: null,
      meetingId: null,
      attendeeId: null,
      topic: null,
      shouldStartVideo: false,
      isActiveMeeting: () => {
        const state = get()
        return Boolean((state.session || state.configuration) && state.meetingId)
      },
      setSession: ({ session, appointmentId, meetingId, attendeeId, topic, startVideo }) =>
        set({
          session,
          appointmentId: appointmentId ?? null,
          meetingId,
          attendeeId,
          topic: topic ?? null,
          shouldStartVideo: Boolean(startVideo)
        }),
      setPreparation: ({ configuration, appointmentId, meetingId, attendeeId, topic }) =>
        set({
          configuration,
          appointmentId: appointmentId ?? null,
          meetingId,
          attendeeId,
          topic: topic ?? null
        }),
      setShouldStartVideo: (value) => set({ shouldStartVideo: value }),
      clearSession: () =>
        set({
          session: null,
          configuration: null,
          appointmentId: null,
          meetingId: null,
          attendeeId: null,
          topic: null,
          shouldStartVideo: false
        }),
      clearMeetingSession: () =>
        set({
          session: null,
          configuration: null,
          shouldStartVideo: false
        })
    }),
    { name: 'MeetingStore', enabled: import.meta.env.DEV }
  )
)
